/**
 * TwinChatPanel (iter1064, разширен iter1066) — разговор с мрежата от близнаци.
 * · `twin` — стеснява разговора до един близнак (панел в неговата страница)
 * · гласов въпрос: записът се разчита и ТЕКСТЪТ се показва за преглед
 * · под всеки отговор: „Запиши като цел за седмицата"
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, Send, MessageSquare, Plus, Trash2, Sparkles, Mic, Square, Target, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const TOKEN_KEY = "nutramilo_access_token";

const readToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

const firstSentence = (text) => {
  const clean = text.replace(/[*#]/g, " ").replace(/\s+/g, " ").trim();
  const m = clean.match(/[^.!?]{15,180}[.!?]/);
  return (m ? m[0] : clean.slice(0, 160)).trim();
};

const Line = ({ text }) => {
  if (!text.trim()) return <div className="h-2" />;
  if (text.startsWith("## ")) {
    return <div className="font-bold mt-2 mb-1">{text.replace(/^##\s+/, "")}</div>;
  }
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <p className="mb-1.5">
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**")
          ? <strong key={i}>{p.slice(2, -2)}</strong>
          : <span key={i}>{p.replace(/^[-·]\s*/, "· ")}</span>
      )}
    </p>
  );
};

export default function TwinChatPanel({ twin = null, embedded = false, focusScope = null }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [showSessions, setShowSessions] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [goalDraft, setGoalDraft] = useState(null); // {idx, text}
  const [sources, setSources] = useState(null);
  const [showSources, setShowSources] = useState(false);
  const endRef = useRef(null);
  const scrollRef = useRef(null);
  const stickRef = useRef(true);
  const [atBottom, setAtBottom] = useState(true);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Превъртаме САМО полето със съобщенията, никога цялата страница —
  // затова потребителят може свободно да се движи, докато отговорът се
  // съставя. Следенето спира, щом сам превърти нагоре в полето.
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    stickRef.current = near;
    setAtBottom(near);
  }, []);

  const scrollToEnd = useCallback(() => {
    stickRef.current = true;
    setAtBottom(true);
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  const loadSessions = useCallback(() => {
    axios.get(`${API}/twin-chat/sessions`)
      .then(({ data }) => setSessions(data.sessions || []))
      .catch(() => {});
  }, [API]);

  useEffect(() => {
    const params = {};
    if (twin) params.twin = twin;
    if (focusScope) params.focus_scope = focusScope;
    axios.get(`${API}/twin-chat/suggestions`, { params })
      .then(({ data }) => setMeta(data)).catch(() => {});
    loadSessions();
  }, [API, twin, focusScope, loadSessions]);

  useEffect(() => {
    if (!stickRef.current) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streaming]);

  useEffect(() => {
    if (!twin) { setSources(null); return; }
    axios.get(`${API}/marker-map/score-sources`, { params: { twin } })
      .then(({ data }) => setSources(data)).catch(() => setSources(null));
  }, [API, twin]);

  const openSession = async (sid) => {
    try {
      const { data } = await axios.get(`${API}/twin-chat/sessions/${sid}`);
      setMessages(data.messages || []);
      setSessionId(sid);
      setShowSessions(false);
    } catch { toast.error("Разговорът не се зареди."); }
  };

  const removeSession = async (sid) => {
    try {
      await axios.delete(`${API}/twin-chat/sessions/${sid}`);
      if (sid === sessionId) { setSessionId(null); setMessages([]); }
      loadSessions();
    } catch { toast.error("Изтриването не мина."); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1200) { toast.error("Записът е твърде кратък."); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "question.webm");
          const { data } = await axios.post(`${API}/twin-chat/transcribe`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) {
            setQuestion((q) => (q ? `${q} ${data.text}` : data.text));
            toast.success("Прегледайте текста и изпратете.");
          } else {
            toast.error("Записът не се разчете.");
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Записът не се разчете.");
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Няма достъп до микрофона.");
    }
  };

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch { /* вече спрян */ }
    setRecording(false);
  };

  const saveGoal = async (text) => {
    const t = (text || "").trim();
    if (!t) return;
    try {
      await axios.post(`${API}/weekly-goals`, {
        text: t, twin: twin || undefined, source: "разговор",
      });
      setGoalDraft(null);
      toast.success("Целта е записана за тази седмица.");
      window.dispatchEvent(new Event("weekly-goal-saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Целта не се записа.");
    }
  };

  const ask = async (text) => {
    const q = (text || question).trim();
    if (!q || streaming) return;
    setQuestion("");
    stickRef.current = true;
    setAtBottom(true);
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const token = readToken();
      const res = await fetch(`${API}/twin-chat/message`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: q, session_id: sessionId, twin: twin || undefined, focus: focusScope || undefined }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let answer = "";
      let sid = sessionId;
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const chunk of chunks) {
          const line = chunk.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          let ev;
          try { ev = JSON.parse(line); } catch { continue; }
          if (ev.session_id && !sid) { sid = ev.session_id; setSessionId(sid); }
          if (ev.error) throw new Error(ev.error);
          if (ev.delta) {
            answer += ev.delta;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: answer };
              return copy;
            });
          }
        }
      }
      if (!answer.trim()) throw new Error("празен отговор");
      loadSessions();
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Отговорът не се получи. Опитайте отново след малко.",
        };
        return copy;
      });
      toast.error("Разговорът прекъсна.");
    } finally {
      setStreaming(false);
    }
  };

  const cardCls = embedded
    ? ""
    : `rounded-2xl border p-4 ${isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}`;

  return (
    <div className={cardCls} data-testid={twin ? `twin-chat-panel-${twin}` : "twin-chat-panel"}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-base font-bold inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-500" />
            {meta?.twin_label_bg ? `Попитайте за „${meta.twin_label_bg}“` : "Разговор с мрежата"}
          </h3>
          <p className="text-xs opacity-70 mt-1 max-w-2xl">
            {meta?.intro_bg
              || "Питайте за процесите в тялото си — отговорът се изгражда от Вашите данни."}
          </p>
          {meta?.composite != null && !twin && (
            <p className="text-[11px] opacity-60 mt-0.5" data-testid="twin-chat-meta">
              Обща оценка {meta.composite}/100 · покритие {meta.coverage_pct}% ·
              {" "}{meta.twins_with_data} близнака с данни
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="twin-chat-new-btn"
            onClick={() => { setSessionId(null); setMessages([]); }}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Нов разговор
          </Button>
          <Button variant="outline" size="sm" data-testid="twin-chat-sessions-btn"
            onClick={() => setShowSessions((v) => !v)}>
            <MessageSquare className="w-3.5 h-3.5 mr-1" /> Минали ({sessions.length})
          </Button>
        </div>
      </div>

      {showSessions && (
        <div className="mt-3 space-y-1.5" data-testid="twin-chat-session-list">
          {sessions.length === 0 && (
            <p className="text-xs opacity-60">Още няма запазени разговори.</p>
          )}
          {sessions.map((s) => (
            <div key={s.session_id}
              className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs ${
                isDark ? "bg-slate-800/60" : "bg-slate-50"}`}>
              <button type="button" className="flex-1 text-left truncate"
                data-testid={`twin-chat-session-${s.session_id}`}
                onClick={() => openSession(s.session_id)}>
                {s.title || "Разговор"}
              </button>
              <button type="button" onClick={() => removeSession(s.session_id)}
                data-testid={`twin-chat-session-delete-${s.session_id}`}
                className="opacity-50 hover:opacity-100">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {sources?.twin && (
        <div className="mt-3" data-testid="score-sources-block">
          <button type="button" onClick={() => setShowSources((v) => !v)}
            data-testid="score-sources-toggle"
            className={`text-[11px] font-bold underline opacity-70 hover:opacity-100`}>
            Какво влиза в тази оценка
          </button>
          {showSources && (
            <div className={`mt-1.5 rounded-xl px-3 py-2 text-[11px] leading-relaxed ${
              isDark ? "bg-slate-800/50" : "bg-slate-50"}`} data-testid="score-sources-list">
              <p className="opacity-70">{sources.note_bg}</p>
              {sources.markers_owned?.length > 0 && (
                <p className="mt-1.5">
                  <strong>Оценява показателите:</strong>{" "}
                  {sources.markers_owned.map((m) => m.label_bg).join(", ")}
                </p>
              )}
              {sources.markers_used?.length > 0 && (
                <p className="mt-1">
                  <strong>Ползва още:</strong>{" "}
                  {sources.markers_used.map((m) => m.label_bg).join(", ")}
                </p>
              )}
              {sources.signal_sources?.length > 0 && (
                <p className="mt-1">
                  <strong>Коригира се от:</strong>{" "}
                  {sources.signal_sources.map((s) => s.label_bg).join(" · ")}
                </p>
              )}
              {sources.pathways?.length > 0 && (
                <p className="mt-1" data-testid="score-sources-pathways">
                  <strong>Отговаря за биологичните пътища:</strong>{" "}
                  {sources.pathways.map((p) => p.name_bg).join(" · ")}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {messages.length === 0 && meta?.suggestions?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="twin-chat-suggestions">
          {meta.suggestions.map((s) => (
            <button key={s.id} type="button" onClick={() => ask(s.text)}
              data-testid={`twin-chat-suggestion-${s.id}`}
              className={`text-[11px] text-left rounded-full px-3 py-1.5 border transition-colors max-w-full whitespace-normal break-words ${
                isDark
                  ? "border-slate-700 hover:border-indigo-500 text-slate-200"
                  : "border-slate-200 hover:border-indigo-400 text-slate-700"}`}>
              {s.text}
            </button>
          ))}
        </div>
      )}

      {messages.length > 0 && (
        <div className="relative">
        <div ref={scrollRef} onScroll={onScroll}
          className="mt-3 space-y-2.5 max-h-[460px] overflow-y-auto pr-1"
          data-testid="twin-chat-messages">
          {messages.map((m, i) => {
            const mine = m.role === "user";
            return (
              <div key={i} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  mine
                    ? "bg-indigo-600 text-white"
                    : isDark ? "bg-slate-800/80 text-slate-100" : "bg-white border border-slate-200 text-slate-800"
                }`} data-testid={`twin-chat-bubble-${m.role}-${i}`}>
                  {mine ? m.text : m.text.split("\n").map((l, j) => <Line key={j} text={l} />)}
                </div>
                {!mine && m.text.trim().length > 40 && !streaming && (
                  goalDraft?.idx === i ? (
                    <div className="mt-1.5 flex items-center gap-2 w-full max-w-[85%]">
                      <input
                        autoFocus
                        value={goalDraft.text}
                        onChange={(e) => setGoalDraft({ idx: i, text: e.target.value })}
                        onKeyDown={(e) => { if (e.key === "Enter") saveGoal(goalDraft.text); }}
                        data-testid={`twin-chat-goal-input-${i}`}
                        className={`flex-1 rounded-xl px-3 py-1.5 text-xs outline-none border ${
                          isDark ? "bg-slate-950/60 border-slate-700" : "bg-white border-slate-200"}`}
                      />
                      <Button size="sm" onClick={() => saveGoal(goalDraft.text)}
                        data-testid={`twin-chat-goal-save-${i}`}>Запишете</Button>
                      <Button size="sm" variant="ghost" onClick={() => setGoalDraft(null)}
                        data-testid={`twin-chat-goal-cancel-${i}`}>Откажете</Button>
                    </div>
                  ) : (
                    <button type="button"
                      onClick={() => setGoalDraft({ idx: i, text: firstSentence(m.text) })}
                      data-testid={`twin-chat-goal-btn-${i}`}
                      className={`mt-1 inline-flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 border transition-colors ${
                        isDark
                          ? "border-emerald-800 text-emerald-300 hover:bg-emerald-900/30"
                          : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>
                      <Target className="w-3 h-3" /> Запиши като цел за седмицата
                    </button>
                  )
                )}
              </div>
            );
          })}
          {streaming && (
            <div className="text-[11px] opacity-60 inline-flex items-center gap-1.5"
              data-testid="twin-chat-typing">
              <Loader2 className="w-3 h-3 animate-spin" /> мрежата съставя отговор…
            </div>
          )}
          <div ref={endRef} />
        </div>
        {!atBottom && (
          <button type="button" onClick={scrollToEnd}
            data-testid="twin-chat-scroll-to-end"
            className={`absolute bottom-3 right-4 z-10 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] border shadow-lg transition-colors ${
              isDark
                ? "bg-slate-900/90 border-slate-700 text-slate-200 hover:bg-slate-800"
                : "bg-white/95 border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
            <ArrowDown className="w-3 h-3" /> Към последното съобщение
          </button>
        )}
        </div>
      )}

      <form className="mt-3 flex items-end gap-2"
        onSubmit={(e) => { e.preventDefault(); ask(); }}>
        <textarea
          rows={2}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); }
          }}
          placeholder={transcribing ? "Разчитам записа…" : "Например: защо кортизолът ми задържа мазнини в корема?"}
          data-testid="twin-chat-input"
          className={`flex-1 min-w-0 rounded-xl px-3 py-2 text-sm resize-none outline-none border ${
            isDark ? "bg-slate-950/60 border-slate-700" : "bg-white border-slate-200"}`}
        />
        <Button type="button" size="icon" className="shrink-0 w-9 h-9" variant={recording ? "destructive" : "outline"}
          onClick={recording ? stopRecording : startRecording}
          disabled={transcribing || streaming}
          title={recording ? "Спрете записа" : "Кажете въпроса на глас"}
          data-testid="twin-chat-mic-btn">
          {transcribing
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </Button>
        <Button type="submit" size="icon" className="shrink-0 w-9 h-9" disabled={streaming || !question.trim()} data-testid="twin-chat-send-btn">
          {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>

      {recording && (
        <p className="text-[11px] text-rose-500 mt-1.5" data-testid="twin-chat-recording">
          Записвам… натиснете квадратчето, за да спрете. Текстът ще се появи за преглед.
        </p>
      )}

      <p className="text-[10px] opacity-50 mt-2">
        Образователно обяснение върху Вашите данни. Не е диагноза и не заменя
        прегледа при лекар.
      </p>
    </div>
  );
}
